import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileDown, Loader2, CheckCircle2, AlertTriangle, Store, FileArchive } from "lucide-react";
import { toast } from "sonner";

export default function DealerImport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef();
  const zipRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [result, setResult] = useState(null);

  if (!user?.is_dealer) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <Store className="w-14 h-14 text-blaze mx-auto" />
        <h1 className="font-heading font-900 text-2xl uppercase tracking-tight mt-6">Dealers only</h1>
        <p className="text-sm text-muted-foreground mt-2">Bulk CSV import is a Verified Dealer feature.</p>
        <Button data-testid="import-become-dealer" onClick={() => navigate("/dealer")} className="mt-6 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold">Become a Dealer</Button>
      </div>
    );
  }

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/dealer/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "fitwise_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (zipFile) fd.append("photos_zip", zipFile);
      const r = await api.post("/dealer/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.created > 0) toast.success(`${r.data.created} listing${r.data.created === 1 ? "" : "s"} imported`);
      if (r.data.created === 0) toast.error("No listings imported — check the errors below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-heading font-900 text-3xl uppercase tracking-tight">Bulk Inventory Import</h1>
        <p className="text-sm text-muted-foreground mt-2">Upload a CSV to publish many listings at once. All imported listings are featured with your dealer badge.</p>
      </div>

      <div className="bg-[#141414] border border-border rounded-sm p-6">
        <div className="flex items-center justify-between gap-3 pb-5 border-b border-border">
          <div>
            <div className="font-heading font-800 text-sm uppercase tracking-tight">Step 1 — Get the template</div>
            <p className="text-xs text-muted-foreground mt-1">Required: title, category (wheels/tires/set), brand, condition, price, location, fulfillment. In photo_url and proof_photo, use image URLs OR filenames that match your photos ZIP (separate multiple with " | ").</p>
          </div>
          <Button data-testid="download-template-btn" onClick={downloadTemplate} variant="outline" className="border-border bg-transparent rounded-sm gap-2 shrink-0"><FileDown className="w-4 h-4" /> Download Template</Button>
        </div>

        <div className="pt-6 border-b border-border pb-6">
          <div className="font-heading font-800 text-sm uppercase tracking-tight mb-3">Step 2 — Attach photos ZIP <span className="text-muted-foreground normal-case font-normal">(optional)</span></div>
          <div className="flex items-center gap-3">
            <Button data-testid="zip-choose-btn" onClick={() => zipRef.current?.click()} variant="outline" className="border-border bg-transparent rounded-sm gap-2"><FileArchive className="w-4 h-4" /> {zipFile ? "Change ZIP" : "Choose photos ZIP"}</Button>
            {zipFile && <span className="text-xs text-emerald-400 spec-block flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {zipFile.name}</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-2">CSV filenames like <span className="spec-block text-blaze">maverick_1.jpg</span> are matched to images in this ZIP and uploaded automatically.</p>
          <input ref={zipRef} type="file" accept=".zip,application/zip" onChange={(e) => setZipFile(e.target.files?.[0] || null)} className="hidden" data-testid="zip-input" />
        </div>

        <div className="pt-6">
          <div className="font-heading font-800 text-sm uppercase tracking-tight mb-3">Step 3 — Upload your CSV</div>
          <button data-testid="import-dropzone" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full border border-dashed border-border rounded-sm py-12 flex flex-col items-center gap-2 text-muted-foreground hover:border-blaze hover:text-blaze transition-colors">
            {uploading ? <Loader2 className="w-8 h-8 animate-spin" /> : <UploadCloud className="w-8 h-8" />}
            <span className="text-sm font-bold uppercase tracking-wider">{uploading ? "Importing…" : "Choose CSV file"}</span>
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-input" />
        </div>
      </div>

      {result && (
        <div data-testid="import-result" className="mt-6 bg-[#141414] border border-border rounded-sm p-5">
          <div className="flex items-center gap-2 text-emerald-400 font-heading font-800 uppercase tracking-tight">
            <CheckCircle2 className="w-5 h-5" /> {result.created} imported
          </div>
          {(result.photos_matched > 0 || result.photos_missing > 0) && (
            <p className="text-xs text-muted-foreground mt-1.5">{result.photos_matched} photo(s) uploaded from ZIP{result.photos_missing > 0 ? ` · ${result.photos_missing} filename(s) not found in ZIP` : ""}</p>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 text-amber-400 text-xs font-bold uppercase tracking-wider mb-2"><AlertTriangle className="w-4 h-4" /> {result.errors.length} row(s) skipped</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-xs text-muted-foreground spec-block">Row {e.row}: {e.error}</div>
                ))}
              </div>
            </div>
          )}
          {result.created > 0 && (
            <Button data-testid="import-view-listings" onClick={() => navigate(`/seller/${user.user_id}`)} className="mt-5 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold">View my listings</Button>
          )}
        </div>
      )}
    </div>
  );
}
